import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import useOrgData from '../../../application/hooks/useOrgData';
import {
  validateEmail,
  validatePhoneNumber,
} from '../../../application/routes/settings/utils/validation';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { FormField } from '../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    id: string;
    imageName?: string;
    name: string;
  };
};

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export function GeneralInformationPage() {
  const [org, setOrg] = useState<any>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    contactEmail?: string;
    contactPhone?: string;
  }>({});

  const { orgData, updateOrgData } = useOrgData();
  const toast = useToast();

  useEffect(() => {
    if (orgData) {
      setOrg(orgData);
    }
  }, [orgData]);

  useEffect(() => {
    if (org && orgData) {
      const fieldsToCompare = [
        'name',
        'contactPhone',
        'contactEmail',
        'contactAddress',
        'contactAddress2',
        'contactCity',
        'contactState',
        'contactZip',
      ];
      const changed = fieldsToCompare.some(
        (field) => (org[field] || '') !== (orgData[field] || '')
      );
      setHasChanged(changed);
    }
  }, [org, orgData]);

  const updateField = useCallback((field: string, value: string) => {
    setOrg((prev: any) => ({ ...prev, [field]: value }));
    if (
      field === 'name' ||
      field === 'contactEmail' ||
      field === 'contactPhone'
    ) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!org?.name || org.name.trim() === '') {
      newErrors.name = 'Organization name is required';
    }
    if (org?.contactEmail && !validateEmail(org.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }
    if (org?.contactPhone && !validatePhoneNumber(org.contactPhone)) {
      newErrors.contactPhone =
        'Please enter a valid phone number (at least 10 digits)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }
    setIsSaving(true);
    try {
      const res = await updateOrgData({
        id: org.id,
        name: org.name,
        contactPhone: org.contactPhone,
        contactEmail: org.contactEmail,
        contactAddress: org.contactAddress,
        contactAddress2: org.contactAddress2,
        contactCity: org.contactCity,
        contactState: org.contactState,
        contactZip: org.contactZip,
      });
      if (res) {
        setHasChanged(false);
        toast.success('General information updated successfully');
      } else {
        toast.error('Error updating general information');
      }
    } catch (err) {
      console.error('Error updating general information:', err);
      toast.error('Error updating general information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (orgData) {
      setOrg(orgData);
    }
  };

  if (!org) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="General information"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="General information"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto">
          {/* About section */}
          <h3 className="text-base font-semibold text-foreground mb-5">
            About
          </h3>

          <div className="space-y-4 [&_label]:text-muted-foreground [&_label]:font-normal">
            {/* Name - full width */}
            <FormField label="Name" error={errors.name}>
              <Input
                value={org.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className={errors.name ? 'border-destructive' : ''}
              />
            </FormField>

            {/* Email + Phone - side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Email" error={errors.contactEmail}>
                <Input
                  type="email"
                  value={org.contactEmail || ''}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  className={errors.contactEmail ? 'border-destructive' : ''}
                />
              </FormField>

              <FormField label="Phone number" error={errors.contactPhone}>
                <Input
                  value={org.contactPhone || ''}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  className={errors.contactPhone ? 'border-destructive' : ''}
                />
              </FormField>
            </div>

            {/* Address - full width */}
            <FormField label="Address">
              <Input
                value={org.contactAddress || ''}
                onChange={(e) => updateField('contactAddress', e.target.value)}
              />
            </FormField>

            {/* Address 1 - full width */}
            <FormField label="Address 1">
              <Input
                value={org.contactAddress2 || ''}
                onChange={(e) => updateField('contactAddress2', e.target.value)}
              />
            </FormField>

            {/* City + State + Zip Code - 3 columns */}
            <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_120px_120px] gap-4">
              <FormField label="City">
                <Input
                  value={org.contactCity || ''}
                  onChange={(e) => updateField('contactCity', e.target.value)}
                />
              </FormField>

              <FormField label="State">
                <Select
                  value={org.contactState || ''}
                  onValueChange={(val) => updateField('contactState', val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Zip Code">
                <Input
                  value={org.contactZip || ''}
                  onChange={(e) => updateField('contactZip', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="grid grid-cols-2 gap-4 mt-10 pb-8">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={!hasChanged || isSaving}
              className="py-2.5 px-6 text-sm font-bold"
            >
              Discard
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanged || isSaving}
              className="py-2.5 px-6 text-sm font-bold"
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
